
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { XCircle, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const PaymentCancelled = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCleaningUp, setIsCleaningUp] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const cleanupCancelledBooking = async () => {
      if (!user) {
        console.log("No user found, skipping cleanup");
        setIsCleaningUp(false);
        return;
      }

      const sessionId = searchParams.get('session_id');
      console.log("Payment cancelled, cleaning up booking for session:", sessionId);

      try {
        // Find and delete any pending payment booking for this user created in the last hour
        const { data: recentBookings, error: fetchError } = await supabase
          .from('bookings')
          .select('id, created_at')
          .eq('client_id', user.id)
          .eq('status', 'Pending Payment')
          .gte('created_at', new Date(Date.now() - 60 * 60 * 1000).toISOString()) // Last hour
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error("Error fetching recent bookings:", fetchError);
          return;
        }

        if (recentBookings && recentBookings.length > 0) {
          // Delete the most recent pending booking
          const bookingToDelete = recentBookings[0];
          const { error: deleteError } = await supabase
            .from('bookings')
            .delete()
            .eq('id', bookingToDelete.id);

          if (deleteError) {
            console.error("Error deleting cancelled booking:", deleteError);
          } else {
            console.log("Successfully cleaned up cancelled booking:", bookingToDelete.id);
            toast({
              title: "预订已取消",
              description: "您的预订已被取消，时间段现在可供其他人预订。",
              variant: "default"
            });
          }
        }
      } catch (error) {
        console.error("Error during cleanup:", error);
      } finally {
        setIsCleaningUp(false);
      }
    };

    cleanupCancelledBooking();
  }, [user, searchParams, toast]);

  const handleGoBackToPractitioners = () => {
    try {
      const temp = sessionStorage.getItem('tempBookingInfo');
      if (temp) {
        const parsed = JSON.parse(temp);
        if (parsed.returnToPath) return navigate(parsed.returnToPath);
        if (parsed.practitionerId) return navigate(`/book-an-appointment/${parsed.practitionerId}`);
      }
    } catch {}
    navigate('/book-an-appointment');
  };

  const handleGoBackToBooking = () => {
    navigate('/booking-datetime');
  };

  if (isCleaningUp) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-warm-50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-2xl">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-warm-50 py-12 px-4 pt-16">
        <div className="container mx-auto max-w-2xl">
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-red-700">
                付款已取消
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div className="text-gray-700">
                <p className="mb-2">
                  您的付款已被取消，预订没有完成。
                </p>
                <p className="text-sm text-gray-600">
                  相关的时间段已经释放，现在可供其他用户预订。
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={handleGoBackToBooking}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  重新选择时间
                </Button>
                <Button 
                  onClick={handleGoBackToPractitioners}
                  className="flex items-center gap-2"
                >
                  浏览其他治疗师
                </Button>
              </div>

              <div className="text-xs text-gray-500 bg-gray-100 p-3 rounded">
                <p>
                  如果您遇到任何问题或需要帮助，请联系我们的客服团队。
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentCancelled;
